import React from 'react';

import { uiConfig } from '@/config';
import { cn } from '@/libs';
import { useDevice, useWindowSize } from '@/ui-kit';

import { FormFieldMapper } from '..';
import { Checkbox, Input } from '../inputs';
import { useFormActions, useFormValues } from '../../core';
import {
  useClearOnNotApplicable,
  useNotApplicableField,
  useShouldValidateField,
} from '../../hooks';
import {
  FormValidation,
  FormFieldType,
  type FormInitialValues,
  type IFormCheckbox,
} from '../../typings';
import type { OfficeHoursFormField } from '../../typings';
import { config } from '@/forms/config';

import './OfficeHoursPresenter.scss';
import { GroupTitle } from '..';
import { FormFieldAlertMessage } from './FormFieldAlertMessage';
import { CopyHoursPopover } from './CopyHoursPopover';
import type { DayOfWeek } from '@/typings';

type OfficeHoursPresenterProps = {
  field: OfficeHoursFormField;
  disabled?: boolean;
};

export const OfficeHoursPresenter: React.FC<OfficeHoursPresenterProps> = ({
  field,
  disabled,
}) => {
  const values = useFormValues<FormInitialValues>();
  const { setFieldValue } = useFormActions();
  const dimensions = useWindowSize();
  const device = useDevice(dimensions);

  const wrapeprClasses = cn('oh__wrapper', uiConfig.grid.maxColumnsSpanClass);

  const containerClasses = cn(
    'oh__main-container',
    'grid',
    'gap-x-[20px]',
    'gap-y-[20px]',
    {
      'grid-cols-1': device === 'mobile',
      'grid-cols-2': device === 'desktop',
    },
    uiConfig.grid.maxColumnsSpanClass,
  );

  const showOnCall = field.showOnCall === true;
  const showClosed = field.showClosed === true;

  const timeRowClasses = cn(
    'grid',
    'grid-cols-2',
    'gap-x-[15px]',
    'gap-y-[20px]',
  );

  const dayFieldsContainerClasses = cn(
    'oh__day-fields',
    'flex',
    'flex-col',
    'gap-y-[12px]',
  );

  const isFieldNA = useNotApplicableField(field.notApplicableField?.fieldId);
  useShouldValidateField(field.fieldId, !isFieldNA);
  useClearOnNotApplicable(field, isFieldNA);

  return (
    <div className={wrapeprClasses}>
      {field.name && <GroupTitle title={field.name} />}
      {field.alertMessage && (
        <FormFieldAlertMessage alertMessage={field.alertMessage} />
      )}
      {field.notApplicableField !== undefined && (
        <Checkbox
          name={field.notApplicableField.fieldId}
          label={field.notApplicableField.label}
          size="small"
          disabled={disabled}
        />
      )}
      {isFieldNA && field.naDetails && (
        <FormFieldMapper field={field.naDetails} disabled={disabled} />
      )}

      <div className={containerClasses}>
        {(Object.entries(field.days) as Array<[DayOfWeek, (typeof field.days)[DayOfWeek]]>).map(([dayKey, day]) => {
          const onCallField = day.fields.find(
            f =>
              f.type === FormFieldType.Checkbox &&
              f.fieldId.endsWith(config.officeHoursOnCallFieldSuffix),
          );
          const onCallCheckbox =
            onCallField && onCallField.type === FormFieldType.Checkbox
              ? (onCallField as IFormCheckbox)
              : undefined;

          const closedField = day.fields.find(
            f =>
              f.type === FormFieldType.Checkbox &&
              f.fieldId.endsWith(config.officeHoursClosedFieldIdentifier),
          );
          const closedCheckbox =
            closedField && closedField.type === FormFieldType.Checkbox
              ? (closedField as IFormCheckbox)
              : undefined;

          const isOnCall =
            showOnCall &&
            onCallCheckbox &&
            values[onCallCheckbox.name] === true;
          const isClosed =
            showClosed &&
            closedCheckbox &&
            values[closedCheckbox.name] === true;
          const timeDisabled = Boolean(
            disabled || isFieldNA || isOnCall || isClosed,
          );
          const timeFields = day.fields.filter(
            f => f.type === FormFieldType.Time,
          );
          const isDayReadonly = day.fields.some(f => f.readonly);

          return (
            <div
              key={`${field.fieldId}_${day.name}`}
              className="oh__day-container"
            >
              <div className="oh__day-name-row">
                <div className="oh__day-name">{day.name}</div>
                <CopyHoursPopover
                  days={field.days}
                  sourceDayKey={dayKey}
                  disabled={disabled || isFieldNA || isDayReadonly}
                />
              </div>
              <div className={dayFieldsContainerClasses}>
                <div className={timeRowClasses}>
                  {timeFields.map(timeField => (
                    <Input.Time
                      key={timeField.name}
                      label={timeField.label}
                      name={timeField.name}
                      required={timeField.validations?.some(
                        v => v.type === FormValidation.Required,
                      )}
                      disabled={timeDisabled || timeField.readonly}
                    />
                  ))}
                </div>
                {showOnCall && onCallCheckbox && (
                  <div className="oh__on-call-row">
                    <Checkbox
                      key={onCallCheckbox.name}
                      label={onCallCheckbox.label}
                      name={onCallCheckbox.name}
                      size={onCallCheckbox.scale}
                      disabled={
                        disabled ||
                        isFieldNA ||
                        isClosed ||
                        onCallCheckbox.readonly
                      }
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        if (e.target.checked) {
                          timeFields.forEach(timeField =>
                            setFieldValue(timeField.name, ''),
                          );
                          if (closedCheckbox) {
                            setFieldValue(closedCheckbox.name, false);
                          }
                        }
                      }}
                    />
                  </div>
                )}
                {showClosed && closedCheckbox && (
                  <div className="oh__closed-row">
                    <Checkbox
                      key={closedCheckbox.name}
                      label={closedCheckbox.label}
                      name={closedCheckbox.name}
                      size={closedCheckbox.scale}
                      disabled={
                        disabled ||
                        isFieldNA ||
                        isOnCall ||
                        closedCheckbox.readonly
                      }
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        if (e.target.checked) {
                          timeFields.forEach(timeField =>
                            setFieldValue(timeField.name, ''),
                          );
                          if (onCallCheckbox) {
                            setFieldValue(onCallCheckbox.name, false);
                          }
                        }
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
